import React, { useRef, useCallback, useEffect } from "react";

import { LexicalEditor, $getRoot, $createTextNode, $createParagraphNode } from "lexical";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import {AutoFocusPlugin} from '@lexical/react/LexicalAutoFocusPlugin';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import OnContentChangePlugin from "./OnContentChangePlugin";
import LoadInitialContentPlugin from "./LoadInitialContentPlugin";

const SAVE_PERIOD_MS = 500;
const DEFAULT_PLACEHOLDER_TEXT = "Start typing here...";


interface TextEditorProps {
  initialText: string;
  onChangeThrottled?: (text: string) => void;
  readOnly?: boolean;
  placeholderText?: string;
};


const TextEditor = ({
  initialText,
  onChangeThrottled,
  placeholderText,
  readOnly,
}: TextEditorProps) => {
  const throttleRef = useRef<NodeJS.Timeout|null>(null);
  const editorRef = useRef<LexicalEditor|null>(null);
  const editorText = useRef<string|null>(initialText);
  const initialConfig = {
    namespace: "TextEditor",
    onError: (e: any) => console.error(e),
    editorState: () => {},
    editable: readOnly !== undefined ? !readOnly : true,
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
        throttleRef.current = null;
      }
    };
  }, []);

  const throttledOnChange = useCallback(async (updatedText: string): Promise<void> => {
    editorText.current = updatedText;
    if (throttleRef.current || updatedText == initialText) {
      return;
    }

    throttleRef.current = setTimeout(async () => {
      if (editorText.current && onChangeThrottled) {
        onChangeThrottled(editorText.current);
      }
      throttleRef.current = null;
    }, SAVE_PERIOD_MS);
  }, [initialText, onChangeThrottled]);

  const onContentChange = (htmlString: any) => {
    throttledOnChange(htmlString);
  };

  const handleClick = (e: any) => {
    e.stopPropagation();
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  return (
    <div
      className="flex flex-col w-full h-full"
      onClick={handleClick}
    >
      <LexicalComposer initialConfig={initialConfig}>
        <div className="relative" >
           <RichTextPlugin
             contentEditable={
               <ContentEditable className="p-2 focus:outline-none" />
             }
             ErrorBoundary={LexicalErrorBoundary}
             placeholder={<span className="text-gray-500 absolute left-2 top-2 italic">{placeholderText ?? DEFAULT_PLACEHOLDER_TEXT}</span>}
           />
        </div>
        <EditorRefPlugin editorRef={editorRef} />
        <LoadInitialContentPlugin initialContent={initialText} />
        <HistoryPlugin />
        <AutoFocusPlugin />
        <OnContentChangePlugin onChange={onContentChange} />
      </LexicalComposer>
    </div>
  );
};

export default TextEditor;
